// src/app/features/chat/useChatSocket.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAccessToken } from "../../state/authSession.js";
import { ORIGIN } from "../../api/http.js";

/**
 * Reliable-ish WS client:
 * - Auto reconnect with exponential backoff
 * - Emits connection status
 *
 * Backend WS URL (recommended):
 *   ws://127.0.0.1:8000/ws/chat?threadId=...&token=...
 *   wss://<prod-host>/ws/chat?threadId=...&token=...
 */
function toWsOrigin(httpOrigin) {
  return String(httpOrigin || "")
    .replace(/^https:\/\//i, "wss://")
    .replace(/^http:\/\//i, "ws://")
    .replace(/\/+$/, "");
}

function stripTrailingSlashes(s) {
  return (s || "").trim().replace(/\/+$/, "");
}

/**
 * Accepts:
 * - VITE_API_WS (optional) like: wss://worldcoins-4odu.onrender.com
 * - Otherwise uses ORIGIN derived from VITE_API_URL in http.js (recommended)
 */
function getWsBase() {
  const envWs = stripTrailingSlashes(import.meta.env.VITE_API_WS || "");
  if (envWs) return envWs;

  // ORIGIN is the host root, not /api
  return toWsOrigin(ORIGIN || "http://127.0.0.1:8000");
}

export function useChatSocket({ threadId, onMessage }) {
  const [status, setStatus] = useState("disconnected"); // connecting|connected|disconnected
  const wsRef = useRef(null);
  const retryRef = useRef({ attempts: 0, timer: null });

  const scheduleReconnect = useCallback(() => {
    if (retryRef.current.timer) return;
    const attempts = retryRef.current.attempts + 1;
    retryRef.current.attempts = attempts;

    const delay = Math.min(30_000, 500 * Math.pow(2, attempts)); // 0.5s, 1s, 2s... up to 30s
    retryRef.current.timer = window.setTimeout(() => {
      retryRef.current.timer = null;
      connect();
    }, delay);
  }, []); // connect is declared below; we'll patch via function hoisting

  const connect = useCallback(() => {
    if (!threadId) return;

    const existing = wsRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    const token = getAccessToken() || "";
    const base = getWsBase();

    // IMPORTANT: WS is NOT under /api
    const url =
      `${base}/ws/chat` +
      `?threadId=${encodeURIComponent(threadId)}` +
      `&token=${encodeURIComponent(token)}`;

    setStatus("connecting");

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      retryRef.current.attempts = 0;
      setStatus("connected");
    };

    ws.onclose = () => {
      setStatus("disconnected");
      scheduleReconnect();
    };

    ws.onerror = () => {
      // close will fire and reconnect will happen
      try {
        ws.close();
      } catch {}
    };

    ws.onmessage = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        onMessage?.(data);
      } catch {
        // ignore malformed messages
      }
    };
  }, [threadId, onMessage, scheduleReconnect]);

  // Fix scheduleReconnect hoist dependency
  // (This is safe because connect is stable via useCallback)
  useEffect(() => {
    // no-op: ensures connect exists when scheduleReconnect runs
  }, [connect]);

  const sendWs = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    connect();
    return () => {
      if (retryRef.current.timer) window.clearTimeout(retryRef.current.timer);
      retryRef.current.timer = null;
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
  }, [connect]);

  return useMemo(
    () => ({ status, sendWs, reconnect: connect }),
    [status, sendWs, connect]
  );
}
